import { useEffect, useSyncExternalStore } from "react";

import { apiClient } from "@/shared/api/api-client";

/**
 * Domínio de polaridade — servido por `GET /api/polarities` do web_api,
 * que lê a MESMA fonte (`core/polarity_domain.json`) que o motor de
 * classificação usa pra validar resposta de IA.
 *
 * Não existe lista fixa aqui de propósito. A versão anterior fixava 4
 * valores minúsculos (`favoravel`…) que nem batiam com o que a esteira
 * grava (`POSITIVO`…) — menção classificada de verdade renderizava
 * `undefined`. Enquanto o domínio não carrega (ou se a rota falhar), o
 * fallback é honesto: o nome de wire cru como rótulo, tom neutro de
 * "não sei" — nunca uma segunda lista pra divergir.
 *
 * **Dois papéis semânticos, e a diferença não é cosmética.** `tone` é o
 * BALDE (Favorável conta como positivo — é assim que `/mentions/summary`
 * soma, e o que a Visão Geral pergunta); `palette` é a COR, uma por
 * polaridade, porque a paleta da marca dá uma cor própria a cada uma das
 * quatro. Pintar pelo `tone` mostraria duas cores onde a marca pede
 * quatro; contar pela `palette` quebraria a agregação. O CSS resolve a
 * `palette` pro token (`--positive`, `--favorable`…).
 */
export type PolarityTone = "positive" | "negative" | "neutral";

export type PolarityPalette =
  | "positive"
  | "favorable"
  | "unfavorable"
  | "negative"
  | "neutral";

export type PolarityScale = "four_level" | "three_level";

export type PolarityInfo = {
  name: string;
  label: string;
  tone: PolarityTone;
  palette: PolarityPalette;
  /** Em quais escalas de empresa esta polaridade é uma ESCOLHA. A
   * partição vem do servidor (`polarity_domain.json` — a mesma fonte
   * que o motor de IA lê); quem filtra por ela é o hook
   * `usePolarityChoices` (e o filtro de menções, pela escala que o hook
   * recebe), nunca este módulo (que é global e serve a EXIBIÇÃO, escala
   * nenhuma esconde menção antiga). */
  scales: PolarityScale[];
};

let domain: PolarityInfo[] = [];
let byName = new Map<string, PolarityInfo>();
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

/** Busca o domínio — no bootstrap da casca e de novo por quem usa
 * `usePolarityDomain` enquanto ele estiver vazio. Falha é tolerada — a UI
 * degrada pros fallbacks acima em vez de quebrar. Duas chamadas ao mesmo
 * tempo dividem a mesma requisição. */
export function loadPolarityDomain(): Promise<void> {
  loading ??= (async () => {
    try {
      domain = await apiClient.polarities();
      byName = new Map(domain.map(entry => [entry.name, entry]));
      listeners.forEach(listener => listener());
    } catch {
      // Sem rede/rota: segue com domínio vazio e fallbacks por nome cru.
    } finally {
      loading = null;
    }
  })();
  return loading;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * O domínio como estado de React, pra tela que PINTA por polaridade.
 *
 * A carga do bootstrap é uma só e não avisa ninguém: se a API estava fora
 * do ar quando a página abriu (reinício do servidor em dev, deploy), o
 * domínio ficava vazio até o F5 — e o dashboard desenhava toda menção
 * como "sem classificação", cinza, e o modo % vazio (2026-10-25). Aqui o
 * componente re-renderiza quando o domínio chega e, se ele ainda está
 * vazio, pede de novo — a cada mudança de `retryWhen` (o dashboard passa
 * o próprio dado: se o dado chegou, a API está de pé).
 */
export function usePolarityDomain(retryWhen?: unknown): PolarityInfo[] {
  const current = useSyncExternalStore(subscribe, () => domain);
  useEffect(() => {
    if (!domain.length) void loadPolarityDomain();
  }, [retryWhen]);
  return current;
}

/** Na ordem de exibição do servidor — quem consome não reordena. */
export function polarityOptions(): PolarityInfo[] {
  return domain;
}

export function polarityLabel(name: string | null): string {
  if (name === null) return "Sem classificação";
  return byName.get(name)?.label ?? name;
}

/** O balde. Use pra CONTAR e pra filtrar ("as negativas"), nunca pra
 * escolher cor — ver o cabeçalho. */
export function polarityTone(name: string | null): PolarityTone | "unclassified" {
  if (name === null) return "unclassified";
  return byName.get(name)?.tone ?? "unclassified";
}

/** A cor. `unclassified` cobre tanto polaridade nula (a IA não
 * classificou) quanto nome que saiu do domínio: nos dois casos a tela
 * não sabe que cor usar, e chutar uma seria inventar classificação. */
export function polarityPalette(
  name: string | null
): PolarityPalette | "unclassified" {
  if (name === null) return "unclassified";
  return byName.get(name)?.palette ?? "unclassified";
}
